/**
 * The right-hand side of SpecLens once there is a result: Review (accept or reject what it
 * found), Takeoff guide (what to count, built from that review), and RFIs (edit, accept, and
 * generate the Word document on the AE template).
 */

import { useMemo, useState, type ReactNode } from "react";
import type { GuideNote, RequirementItem, RfiRecommendation, SolutionPackage } from "@plan-forge/speclens-contract";
import { buildRfiDocx, rfiFileName } from "./rfi-docx.ts";
import {
  acceptedRfiItems,
  buildGuide,
  citationText,
  decisionKey,
  guideText,
  rfiHeading,
  rfiText,
  type DecisionKind,
  type DecisionEntry,
  type RfiEdit,
  type RfiFields,
  type SpecLensProject,
  type Verdict,
} from "./speclens-project.ts";
import { highlightSegments, statusLabel } from "./speclens-view.ts";

type Tab = "review" | "guide" | "rfis";

interface SpecLensReviewProps {
  readonly project: SpecLensProject;
  readonly onDecide: (kind: DecisionKind, id: string, verdict: Verdict | null, notes: string) => void;
  readonly makers: readonly string[];
  readonly onJudgeMaker: (name: string, verdict: "approved" | "rejected") => void;
  readonly onEditRfi: (id: string, edit: RfiEdit) => void;
  readonly onRfiFields: (fields: Partial<RfiFields>) => void;
  readonly today: string;
}

function download(name: string, type: string, content: BlobPart): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

export function SpecLensReview({ project, onDecide, makers, onJudgeMaker, onEditRfi, onRfiFields, today }: SpecLensReviewProps) {
  const [tab, setTab] = useState<Tab>("review");
  const result = project.result;
  const guide = useMemo(() => (result === null ? null : buildGuide(result, project.decisions)), [result, project.decisions]);
  if (result === null || guide === null) return null;

  const accepted = acceptedRfiItems(result, project.decisions, project.rfiEdits).length;

  return (
    <section className="sl-results">
      <div className="sl-tabs" role="tablist">
        {(
          [
            ["review", `Review (${result.requirements.length + result.notes.length})`],
            ["guide", "Takeoff guide"],
            ["rfis", `RFIs (${accepted} of ${result.rfis.length} accepted)`],
          ] as const
        ).map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? "sl-tab sl-tab--on" : "sl-tab"} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </div>

      {result.errors.length > 0 && (
        <ul className="sl__problem">
          {result.errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}

      {result.coverage !== undefined && result.coverage.length > 0 && (
        <details className="sl-coverage">
          <summary>
            {result.coverage.length} section{result.coverage.length === 1 ? "" : "s"} read, {result.coverage.filter((entry) => entry.status === "products").length} with products
          </summary>
          <ul>
            {result.coverage.map((entry) => (
              <li key={entry.section}>
                {entry.section} {entry.title}: {entry.clauses} clauses,{" "}
                {entry.status === "products" ? `${entry.requirements} requirement${entry.requirements === 1 ? "" : "s"}` : entry.status === "no-product" ? "no product found - check this section by hand" : "not a product section (cabling, racks or another division)"}
              </li>
            ))}
          </ul>
        </details>
      )}

      {result.candidates !== undefined && result.candidates.length > 0 && (
        <section className="sl-card sl-makers">
          <h2 className="sl-card__title">Makers the spec names that SpecLens does not know</h2>
          <p>Approve a real manufacturer and its models are read everywhere, for every project. Reject a phrase that is not one.</p>
          <ul>
            {result.candidates.map((candidate) => (
              <li key={candidate.name}>
                <strong>{candidate.name}</strong> ({citationText(candidate.citation)}): {candidate.evidence}{" "}
                <button type="button" className="btn" onClick={() => onJudgeMaker(candidate.name, "approved")}>Approve maker</button>{" "}
                <button type="button" className="btn" onClick={() => onJudgeMaker(candidate.name, "rejected")}>Not a maker</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {makers.length > 0 && (
        <details className="sl-coverage">
          <summary>{makers.length} approved maker{makers.length === 1 ? "" : "s"} added to the built-in list</summary>
          <ul>
            {makers.map((name) => (
              <li key={name}>
                {name}{" "}
                <button type="button" className="btn" onClick={() => onJudgeMaker(name, "rejected")}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}

      {tab === "review" && <ReviewTab project={project} onDecide={onDecide} onOpenRfis={() => setTab("rfis")} />}
      {tab === "guide" && (
        <section className="sl-card">
          <div className="sl-card__head">
            <h2 className="sl-card__title">Takeoff guide</h2>
            <button
              type="button"
              className="btn"
              onClick={() => download(`${project.name === "" ? "SpecLens" : project.name} takeoff guide.txt`, "text/plain;charset=utf-8", guideText(project.name, guide))}
            >
              Download as text
            </button>
          </div>
          <GuideView project={project} />
        </section>
      )}
      {tab === "rfis" && <RfiTab project={project} onDecide={onDecide} onEditRfi={onEditRfi} onRfiFields={onRfiFields} today={today} />}
    </section>
  );
}

interface DeciderProps {
  readonly entry: DecisionEntry | undefined;
  readonly acceptLabel: string;
  readonly rejectLabel: string;
  /** What the badge says once accepted, when that differs from the button. */
  readonly acceptedLabel?: string;
  readonly onDecide: (verdict: Verdict | null, notes: string) => void;
}

function Decider({ entry, acceptLabel, rejectLabel, acceptedLabel, onDecide }: DeciderProps) {
  const [notes, setNotes] = useState(entry?.notes ?? "");
  return (
    <div className="sl-decider">
      <div className="sl__row">
        {entry === undefined ? (
          <>
            <button type="button" className="btn btn--primary" onClick={() => onDecide("Accepted", notes)}>
              {acceptLabel}
            </button>
            <button type="button" className="btn" onClick={() => onDecide("Rejected", notes)}>
              {rejectLabel}
            </button>
          </>
        ) : (
          <>
            <span className={entry.verdict === "Accepted" ? "sl-badge sl-badge--ok" : "sl-badge sl-badge--no"}>
              {entry.verdict === "Accepted" ? (acceptedLabel ?? acceptLabel) : rejectLabel}
            </span>
            <button type="button" className="btn btn--quiet" onClick={() => onDecide(null, notes)}>
              Change
            </button>
          </>
        )}
      </div>
      <textarea
        className="sl-notes"
        aria-label="Reviewer notes"
        placeholder="Notes"
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        onBlur={() => {
          if (entry !== undefined && notes !== entry.notes) onDecide(entry.verdict, notes);
        }}
      />
    </div>
  );
}

function Excerpt({ text, terms }: { readonly text: string; readonly terms: readonly string[] }) {
  return (
    <p className="sl-excerpt">
      {highlightSegments(text, terms).map((segment, index) => (segment.hit ? <mark key={index}>{segment.text}</mark> : segment.text))}
    </p>
  );
}

function ReviewTab({ project, onDecide, onOpenRfis }: Pick<SpecLensReviewProps, "project" | "onDecide"> & { readonly onOpenRfis: () => void }) {
  const result = project.result;
  if (result === null) return null;

  const rfiFor = new Map(result.rfis.map((rfi) => [rfi.itemId, rfi]));
  const included = acceptedRfiItems(result, project.decisions, project.rfiEdits).length;

  const bySection = new Map<string, RequirementItem[]>();
  for (const item of result.requirements) {
    const key = `${item.citation.section} ${item.citation.sectionTitle}`.trim();
    bySection.set(key, [...(bySection.get(key) ?? []), item]);
  }

  return (
    <>
      <dl className="sl-summary">
        <div>
          <dt>Requirements</dt>
          <dd>{result.summary.requirements}</dd>
        </div>
        <div>
          <dt>With an AE product</dt>
          <dd>{result.summary.withAeProduct}</dd>
        </div>
        <div>
          <dt>No AE product yet</dt>
          <dd>{result.summary.withoutAeProduct}</dd>
        </div>
        <div>
          <dt>Notes to check</dt>
          <dd>{result.notes.length}</dd>
        </div>
        <div>
          <dt>Draft RFIs</dt>
          <dd>{result.summary.rfis}</dd>
        </div>
      </dl>

      {result.rfis.length > 0 && (
        <section className="sl-card sl-rfi-bar">
          <p>
            <strong>{included}</strong> of {result.rfis.length} suggested RFIs included.
          </p>
          <button type="button" className="btn btn--primary" onClick={onOpenRfis}>
            {included > 0 ? "Review and generate the RFI document" : "Review the suggested RFIs"}
          </button>
        </section>
      )}

      {[...bySection].map(([heading, items]) => (
        <section key={heading} className="sl-card">
          <h2 className="sl-card__title">{heading}</h2>
          {items.map((item) => (
            <RequirementCard
              key={item.itemId}
              item={item}
              entry={project.decisions[decisionKey("requirement", item.itemId)]}
              rfi={rfiFor.get(item.itemId)}
              rfiEntry={project.decisions[decisionKey("rfi", rfiFor.get(item.itemId)?.rfiId ?? "")]}
              onDecide={onDecide}
              onOpenRfis={onOpenRfis}
            />
          ))}
        </section>
      ))}

      {result.notes.length > 0 && (
        <section className="sl-card">
          <details className="sl-notes-fold">
            <summary>
              Notes to check ({result.notes.length}) - watch-outs, existing equipment, conflicts, and requirements counted once
            </summary>
            <p className="hint">Remove a note that does not apply; the takeoff guide lists the rest.</p>
            {result.notes.map((note) => (
              <NoteRow
                key={note.noteId}
                note={note}
                entry={project.decisions[decisionKey("note", note.noteId)]}
                rfi={rfiFor.get(note.noteId)}
                rfiEntry={project.decisions[decisionKey("rfi", rfiFor.get(note.noteId)?.rfiId ?? "")]}
                onDecide={onDecide}
              />
            ))}
          </details>
        </section>
      )}
    </>
  );
}

/** Which answers are AE's own record and which are SpecLens's judgement, so a reviewer knows where to look. */
const SOURCE_LABELS: Readonly<Record<NonNullable<RequirementItem["translation"]["source"]>, string>> = {
  "named model": "Named in the spec",
  "AE mapping": "AE mapping",
  "design standard": "Design standard - confirm",
  attributes: "Closest match on values - confirm",
  function: "Matched by function - confirm",
};

/** The RFI drafted for a requirement or note: a switch the reviewer can flip without leaving the card. */
function RfiOffer({
  rfi,
  entry,
  onDecide,
  onOpenRfis,
}: {
  readonly rfi: RfiRecommendation;
  readonly entry: DecisionEntry | undefined;
  readonly onDecide: SpecLensReviewProps["onDecide"];
  readonly onOpenRfis?: () => void;
}) {
  const included = entry?.verdict === "Accepted";
  return (
    <div className="sl-rfi-offer">
      <p className="sl-label">Suggested RFI</p>
      <p>{rfi.issue}</p>
      <div className="sl__row">
        {included ? (
          <>
            <span className="sl-badge sl-badge--ok">Added to RFI</span>
            <button type="button" className="btn btn--quiet" onClick={() => onDecide("rfi", rfi.rfiId, null, "")}>
              Take out
            </button>
          </>
        ) : (
          <button type="button" className="btn" onClick={() => onDecide("rfi", rfi.rfiId, "Accepted", entry?.notes ?? "")}>
            Add to RFI
          </button>
        )}
        {onOpenRfis !== undefined && (
          <button type="button" className="btn btn--quiet" onClick={onOpenRfis}>
            Edit RFI
          </button>
        )}
      </div>
    </div>
  );
}

function RequirementCard({
  item,
  entry,
  rfi,
  rfiEntry,
  onDecide,
  onOpenRfis,
}: {
  readonly item: RequirementItem;
  readonly entry: DecisionEntry | undefined;
  readonly rfi: RfiRecommendation | undefined;
  readonly rfiEntry: DecisionEntry | undefined;
  readonly onDecide: SpecLensReviewProps["onDecide"];
  readonly onOpenRfis: () => void;
}) {
  const t = item.translation;
  if (entry?.verdict === "Rejected") {
    return (
      <article className="sl-req sl-req--rejected">
        <span className="sl-badge sl-badge--no">Rejected</span>
        <span>
          {item.productLabel} <span className="hint">{citationText(item.citation)}</span>
        </span>
        <button type="button" className="btn btn--quiet" onClick={() => onDecide("requirement", item.itemId, null, entry.notes)}>
          Put back
        </button>
      </article>
    );
  }
  return (
    <article className="sl-req">
      <header className="sl-req__head">
        <h3>{item.productLabel}</h3>
        {item.scope !== "other" && <span className="sl-scope">{item.scope}</span>}
        <span className="hint">
          {citationText(item.citation)}
          {item.context !== undefined && item.context !== "" ? ` - ${item.context}` : ""}
        </span>
      </header>

      <div className="sl-req__body">
        <div>
          <p className="sl-label">The spec asks for</p>
          {item.quantityHint !== "" && <p>{item.quantityHint}</p>}
          {item.attributes.length > 0 ? (
            <ul className="sl-attrs">
              {item.attributes.map((attribute) => (
                <li key={attribute.name}>
                  <span>{attribute.name}</span> {attribute.value}
                </li>
              ))}
            </ul>
          ) : (
            <p className="hint">No measurable attributes in this clause.</p>
          )}
          {item.namedProducts.length > 0 && (
            <p className="hint">
              Names: {item.namedProducts.map((product) => `${`${product.manufacturer} ${product.model}`.trim()} (${product.role})`).join(", ")}
            </p>
          )}
        </div>

        <div>
          <p className="sl-label">Audio Enhancement</p>
          <p className={`sl-status sl-status--${t.status}`}>
            {t.aeProduct === "" ? statusLabel(t.status) : `${t.aeProduct} - ${t.aeProductName}`}
          </p>
          {t.aeProduct !== "" && (
            <p className="hint">
              {statusLabel(t.status)}
              {t.source !== undefined && (
                <span className={t.source === "named model" || t.source === "AE mapping" ? "sl-source" : "sl-source sl-source--judged"}>
                  {SOURCE_LABELS[t.source]}
                </span>
              )}
            </p>
          )}
          {t.differences.length > 0 && (
            <ul className="sl-diffs">
              {t.differences.map((difference) => (
                <li key={difference}>{difference}</li>
              ))}
            </ul>
          )}
          {t.aeProduct !== "" && !t.reviewed && (
            <p className="hint">
              {t.functional === true
                ? "Matched by the job it does, not yet approved by a design engineer."
                : "This catalog row has not been approved by a design engineer yet."}
            </p>
          )}
          <p className="hint">{t.basis}</p>
        </div>
      </div>

      <details>
        <summary>Spec text</summary>
        <Excerpt text={item.excerpt} terms={item.highlightTerms} />
      </details>

      {rfi !== undefined && <RfiOffer rfi={rfi} entry={rfiEntry} onDecide={onDecide} onOpenRfis={onOpenRfis} />}

      <Decider
        entry={entry}
        acceptLabel="Add to Project"
        rejectLabel="Reject"
        acceptedLabel="Added to Project"
        onDecide={(verdict, notes) => {
          onDecide("requirement", item.itemId, verdict, notes);
          // Rejecting the requirement takes its RFI out of the document, so a question about
          // equipment the reviewer ruled out is never sent.
          if (rfi !== undefined && verdict === "Rejected" && rfiEntry?.verdict === "Accepted") {
            onDecide("rfi", rfi.rfiId, null, "");
          }
        }}
      />
    </article>
  );
}

const NOTE_LABELS: Readonly<Record<GuideNote["category"], string>> = {
  "watch-out": "Watch-out",
  existing: "Existing",
  conflict: "Conflict",
  duplication: "Duplication",
};

function NoteRow({
  note,
  entry,
  rfi,
  rfiEntry,
  onDecide,
}: {
  readonly note: GuideNote;
  readonly entry: DecisionEntry | undefined;
  readonly rfi: RfiRecommendation | undefined;
  readonly rfiEntry: DecisionEntry | undefined;
  readonly onDecide: SpecLensReviewProps["onDecide"];
}) {
  return (
    <article className="sl-note">
      <header className="sl-note__head">
        <span className={`sl-badge sl-badge--${note.category}`}>{NOTE_LABELS[note.category]}</span>
        <strong>{note.title}</strong>
        <span className="hint">{note.citations.map(citationText).join("; ")}</span>
      </header>
      {note.detail !== "" && <p>{note.detail}</p>}
      {note.excerpt !== "" && <Excerpt text={note.excerpt} terms={note.highlightTerms} />}
      {rfi !== undefined && <RfiOffer rfi={rfi} entry={rfiEntry} onDecide={onDecide} />}
      <div className="sl__row">
        {entry?.verdict === "Rejected" ? (
          <>
            <span className="sl-badge sl-badge--no">Removed</span>
            <button type="button" className="btn btn--quiet" onClick={() => onDecide("note", note.noteId, null, "")}>
              Put back
            </button>
          </>
        ) : (
          <button type="button" className="btn btn--quiet" onClick={() => onDecide("note", note.noteId, "Rejected", "")}>
            Remove from guide
          </button>
        )}
      </div>
    </article>
  );
}

function SolutionTable({ entry }: { readonly entry: SolutionPackage }) {
  return (
    <div className="sl-solution">
      <h4>{entry.title}</h4>
      <p className="hint">{entry.basis}</p>
      <table className="sl-table">
        <thead>
          <tr>
            <th>Part</th>
            <th>Model</th>
            <th>Qty</th>
            <th>In the spec?</th>
          </tr>
        </thead>
        <tbody>
          {entry.lines.map((part) => (
            <tr key={`${part.role}-${part.part}`}>
              <td>
                <strong>{part.part}</strong>
                {!part.inCatalog && <span className="sl-source sl-source--judged">Not in the reviewed catalog yet</span>}
              </td>
              <td>
                {part.model}
                <div className="hint">
                  {part.role}: {part.name}
                </div>
              </td>
              <td>{part.quantity}</td>
              <td>
                {part.spec === "added" ? (
                  "Standard adds it"
                ) : (
                  <>
                    {part.spec === "differs" ? <strong>The spec asks for something different</strong> : "Yes"}
                    <div className="hint">
                      {part.specText}
                      {part.citation !== undefined && ` (${citationText(part.citation)})`}
                    </div>
                    {part.specAnswer !== "" && <div>The spec's own clause is answered by {part.specAnswer}</div>}
                    {part.differences.length > 0 && (
                      <ul className="sl-diffs">
                        {part.differences.map((difference) => (
                          <li key={difference}>{difference}</li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
                {part.note !== "" && <div className="hint">{part.note}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {entry.specOnly.length > 0 && (
        <p className="hint">The spec also lists, with no standard part: {entry.specOnly.join("; ")}</p>
      )}
    </div>
  );
}

/** The Design Standards by solution, with the areas the spec names first and the rest as reference. */
function SolutionTables({ solutions }: { readonly solutions: readonly SolutionPackage[] }) {
  if (solutions.length === 0) return null;
  const single = solutions.filter((entry) => entry.solution !== "EPIC intercom speakers");
  const areas = solutions.filter((entry) => entry.solution === "EPIC intercom speakers");
  const named = areas.filter((entry) => entry.lines.some((part) => part.spec === "listed"));
  const reference = areas.filter((entry) => !entry.lines.some((part) => part.spec === "listed"));
  return (
    <div className="sl-guide__part">
      <h3>Standard package by solution</h3>
      <p className="hint">From the AE Design Standards, by model and by part number, set against what the spec lists.</p>
      {single.map((entry) => (
        <SolutionTable key={entry.title} entry={entry} />
      ))}
      <h3>EPIC intercom speakers by area</h3>
      {named.length === 0 && <p className="hint">The spec names none of the standard areas; the reference list is below.</p>}
      {named.map((entry) => (
        <SolutionTable key={entry.title} entry={entry} />
      ))}
      {reference.length > 0 && (
        <details className="sl-notes-fold">
          <summary>Other areas in the standards ({reference.length})</summary>
          {reference.map((entry) => (
            <SolutionTable key={entry.title} entry={entry} />
          ))}
        </details>
      )}
    </div>
  );
}

function GuideView({ project }: { readonly project: SpecLensProject }) {
  const result = project.result;
  const guide = useMemo(() => (result === null ? null : buildGuide(result, project.decisions)), [result, project.decisions]);
  if (guide === null) return null;

  const section = (title: string, notes: readonly GuideNote[]): ReactNode =>
    notes.length === 0 ? null : (
      <div className="sl-guide__part">
        <h3>{title}</h3>
        <ul>
          {notes.map((note) => (
            <li key={note.noteId}>
              <strong>{note.title}</strong> <span className="hint">({citationText(note.citations[0] ?? { source: "", section: "", sectionTitle: "", page: 0, sectionPage: 0, reference: "" })})</span>
              <br />
              {note.excerpt}
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <div className="sl-guide">
      <SolutionTables solutions={guide.solutions} />

      {guide.package.length > 0 && (
        <div className="sl-guide__part">
          <h3>What the spec lists for each classroom</h3>
          <ul>
            {guide.package.map((row) => (
              <li key={row.lineId}>
                {row.text} {row.aeProduct !== "" && <>&rarr; <strong>{row.aeProduct}</strong></>}{" "}
                <span className="hint">({citationText(row.citation)})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {guide.equipment.length > 0 && (
        <div className="sl-guide__part">
          <h3>Equipment to use</h3>
          {guide.equipment.map((group) => (
            <div key={group.aeProduct || "none"} className="sl-equip">
              <h4>{group.aeProduct === "" ? "No Audio Enhancement product yet" : `${group.aeProduct} - ${group.aeProductName}`}</h4>
              <ul>
                {group.answers.map((answer) => (
                  <li key={answer.itemId}>
                    {answer.label} <span className="hint">({citationText(answer.citation)})</span>
                    {answer.quantityHint !== "" && <> - {answer.quantityHint}</>}
                    {answer.differences.length > 0 && <div className="hint">{answer.differences.join("; ")}</div>}
                  </li>
                ))}
                {group.symbols.map((symbol) => (
                  <li key={symbol.code}>
                    Count symbol <strong>{symbol.code}</strong> ({symbol.description.toLowerCase()}) on{" "}
                    {symbol.sheets.length > 0 ? symbol.sheets.join(", ") : "the plan sheets"}; legend on {symbol.legendSheet}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {section("Watch-outs", guide.watchOuts)}
      {section("Existing equipment", guide.existing)}
      {section("Conflicts", guide.conflicts)}
      {section("Duplications", guide.duplications)}
    </div>
  );
}

function RfiTab({
  project,
  onDecide,
  onEditRfi,
  onRfiFields,
  today,
}: Pick<SpecLensReviewProps, "project" | "onDecide" | "onEditRfi" | "onRfiFields" | "today">) {
  const result = project.result;
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  if (result === null) return null;

  const items = acceptedRfiItems(result, project.decisions, project.rfiEdits);

  const generate = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const bytes = await buildRfiDocx({
        projectLine: project.name,
        to: project.rfi.to,
        from: project.rfi.from,
        issueDate: project.rfi.issueDate === "" ? today : project.rfi.issueDate,
        signature: project.rfi.signature,
        items,
        specSheetsIncluded: project.rfi.specSheetsIncluded,
      });
      download(rfiFileName(project.name), "application/vnd.openxmlformats-officedocument.wordprocessingml.document", new Uint8Array(bytes));
    } catch {
      setFailure("The RFI document could not be created.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="sl-card">
        <h2 className="sl-card__title">RFI document</h2>
        <p className="hint">
          Built from the RFIs you accept below, on the Audio Enhancement RFI template. Nothing is sent anywhere: the document is
          created here and downloaded for you to review and send.
        </p>
        <div className="sl-form">
          <label className="field">
            <span>To (GC or installer)</span>
            <input type="text" value={project.rfi.to} onChange={(event) => onRfiFields({ to: event.target.value })} />
          </label>
          <label className="field">
            <span>From</span>
            <input type="text" value={project.rfi.from} onChange={(event) => onRfiFields({ from: event.target.value })} />
          </label>
          <label className="field">
            <span>Issue date</span>
            <input type="text" value={project.rfi.issueDate} placeholder={today} onChange={(event) => onRfiFields({ issueDate: event.target.value })} />
          </label>
          <label className="field">
            <span>Signature name</span>
            <input type="text" value={project.rfi.signature} onChange={(event) => onRfiFields({ signature: event.target.value })} />
          </label>
        </div>
        <label className="sl-check">
          <input type="checkbox" checked={project.rfi.specSheetsIncluded} onChange={(event) => onRfiFields({ specSheetsIncluded: event.target.checked })} />
          <span>Say that specification sheets are included with the RFI</span>
        </label>
        {failure !== null && (
          <p className="sl__problem" role="alert">
            {failure}
          </p>
        )}
        <button type="button" className="btn btn--primary" disabled={busy || items.length === 0} onClick={() => void generate()}>
          {busy ? "Creating..." : `Generate RFI document (${items.length} item${items.length === 1 ? "" : "s"})`}
        </button>
        {items.length === 0 && <p className="hint">Accept at least one RFI below.</p>}
      </section>

      {result.rfis.length === 0 ? (
        <section className="sl-card">
          <p className="hint">No RFIs were drafted: every requirement either matched an Audio Enhancement product directly or needs a catalog row first.</p>
        </section>
      ) : (
        result.rfis.map((rfi) => (
          <RfiCard
            key={rfi.rfiId}
            rfi={rfi}
            edit={project.rfiEdits[rfi.rfiId]}
            entry={project.decisions[decisionKey("rfi", rfi.rfiId)]}
            onDecide={onDecide}
            onEdit={onEditRfi}
          />
        ))
      )}
    </>
  );
}

function RfiCard({
  rfi,
  edit,
  entry,
  onDecide,
  onEdit,
}: {
  readonly rfi: RfiRecommendation;
  readonly edit: RfiEdit | undefined;
  readonly entry: DecisionEntry | undefined;
  readonly onDecide: SpecLensReviewProps["onDecide"];
  readonly onEdit: SpecLensReviewProps["onEditRfi"];
}) {
  const text = rfiText(rfi, edit);
  return (
    <article className="sl-card sl-rfi">
      <h3 className="sl-rfi__heading">{rfiHeading(rfi)}</h3>
      <label className="field">
        <span>What the spec says</span>
        <textarea value={text.issue} rows={3} onChange={(event) => onEdit(rfi.rfiId, { issue: event.target.value })} />
      </label>
      <label className="field">
        <span>What Audio Enhancement proposes</span>
        <textarea value={text.proposal} rows={3} onChange={(event) => onEdit(rfi.rfiId, { proposal: event.target.value })} />
      </label>
      <label className="field">
        <span>Question</span>
        <textarea value={text.question} rows={2} onChange={(event) => onEdit(rfi.rfiId, { question: event.target.value })} />
      </label>
      <Decider
        entry={entry}
        acceptLabel="Include in RFI"
        rejectLabel="Do not send"
        onDecide={(verdict, notes) => onDecide("rfi", rfi.rfiId, verdict, notes)}
      />
    </article>
  );
}
